/**
 * The list's results: exactly one of the failure, the spinner, the empty
 * state, or the rows. The rows are drawn as both a table and cards; the
 * stylesheet shows the table on desktop and the cards (two columns on tablet,
 * one on phone) elsewhere.
 */
import { Spinner } from '../../dashboards/primitives';
import { EmptyState } from '../../dashboards/primitives/EmptyState';
import {
  campaignEmptyHint,
  campaignEmptyTitle,
  type CampaignListRow,
  type CampaignListTab,
} from '../ticket-sales-campaigns-list-model';

import { TicketSalesCampaignCard } from './TicketSalesCampaignCard';
import { TicketSalesCampaignsLoadError } from './TicketSalesCampaignsLoadError';
import { TicketSalesCampaignsTable } from './TicketSalesCampaignsTable';

import './ticket-sales-campaigns-list.css';

export interface TicketSalesCampaignsResultsBodyProps {
  rows: readonly CampaignListRow[];
  now: Date;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  tab: CampaignListTab;
  search: string;
  /** May create and edit campaigns, so rows open the editor and the hints offer creating one. */
  canWrite: boolean;
}

export function TicketSalesCampaignsResultsBody({
  rows,
  now,
  loading,
  error,
  onRetry,
  tab,
  search,
  canWrite,
}: TicketSalesCampaignsResultsBodyProps) {
  if (error) return <TicketSalesCampaignsLoadError error={error} onRetry={onRetry} />;
  if (loading) {
    return (
      <div role="status" style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <Spinner size={22} />
        <span className="nb-sr-only">Loading campaigns</span>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        title={campaignEmptyTitle(tab, search)}
        hint={campaignEmptyHint(tab, search, canWrite)}
      />
    );
  }
  return (
    <>
      <div className="tsc-table-view">
        <TicketSalesCampaignsTable rows={rows} now={now} canOpen={canWrite} />
      </div>
      <div className="tsc-cards">
        {rows.map((row) => (
          <TicketSalesCampaignCard key={row.campaign.id} row={row} now={now} canOpen={canWrite} />
        ))}
      </div>
    </>
  );
}
